import React, { useEffect, useMemo, useState } from 'react';
import { tDoc, tSubCollection } from '../../../firebase';
import { TENANT_ID } from "../../../tenant";
import { onSnapshot } from 'firebase/firestore';
import {
  Megaphone,
  AlertTriangle,
  Info,
  Link as LinkIcon,
} from 'lucide-react';

const getRemaining = (e) => {
  const total = parseInt(e?.quantity_total ?? 0, 10);
  const used = parseInt(e?.quantity_used ?? 0, 10);
  const pending = parseInt(e?.pending_quantity ?? 0, 10);
  return Math.max(0, total - used - pending);
};

const DashboardMessageBlock = ({
  customerUid,
  profile,
  rawPaymentHistory,
  className = '',
}) => {
  const [dash, setDash] = useState(null);
  const [entitlements, setEntitlements] = useState([]);

  // -------------------------
  // dashboard_config 読み取り
  // -------------------------
  useEffect(() => {
    const ref = tDoc('config', 'dashboard_config');

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setDash(null);
          return;
        }
        const d = snap.data() || {};
        setDash({
          active: !!d.active,
          message: d.message || '',
          type: d.type || 'info',
          link: d.link || '',
          link_text: d.link_text || '',
          target_attribute: d.target_attribute || 'all',
          exclude_door_id: d.exclude_door_id || '',
          require_purchase_category: d.require_purchase_category || '',
        });
      },
      () => setDash(null)
    );

    return () => unsub();
  }, []);

  // -------------------------
  // entitlements 読み取り
  // -------------------------
  useEffect(() => {
    if (!customerUid) {
      setEntitlements([]);
      return;
    }

    const col = tSubCollection('customers', String(customerUid), 'entitlements');
    const unsub = onSnapshot(
      col,
      (snap) => {
        setEntitlements(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      () => setEntitlements([])
    );

    return () => unsub();
  }, [customerUid]);

  // -------------------------
  // 表示判定
  // -------------------------

  const msgTypeStyles = useMemo(
    () => ({
      info: {
        bg: 'bg-gray-50',
        text: 'text-gray-900',
        border: 'border-gray-200',
        icon: <Info size={18} />,
      },
      warning: {
        bg: 'bg-amber-50',
        text: 'text-amber-900',
        border: 'border-amber-200',
        icon: <AlertTriangle size={18} />,
      },
      alert: {
        bg: 'bg-red-50',
        text: 'text-red-800',
        border: 'border-red-200',
        icon: <Megaphone size={18} />,
      },
    }),
    []
  );

  const matchesTargetAttribute = useMemo(() => {
    if (!dash) return false;
    if (dash.target_attribute === 'all') return true;
    return true;
  }, [dash]);

  const isExcludedByDoor = useMemo(() => {
    const excludeId = String(dash?.exclude_door_id || '').trim();
    if (!excludeId) return false;

    return entitlements.some((e) => {
      const doorIds = Array.isArray(e?.door_ids) ? e.door_ids.map(String) : [];
      if (!doorIds.includes(excludeId)) return false;
      return getRemaining(e) > 0 || e?.is_active === true;
    });
  }, [dash?.exclude_door_id, entitlements]);

  const hasRequiredPurchaseCategory = useMemo(() => {
    if (!dash?.require_purchase_category) return true;
    return true;
  }, [dash]);

  const shouldShow = useMemo(() => {
    if (!dash) return false;
    if (!dash.active) return false;
    if (!dash.message?.trim()) return false;
    if (!matchesTargetAttribute) return false;
    if (isExcludedByDoor) return false;
    if (!hasRequiredPurchaseCategory) return false;
    return true;
  }, [dash, matchesTargetAttribute, isExcludedByDoor, hasRequiredPurchaseCategory]);

  if (!shouldShow) return null;

  const style = msgTypeStyles[dash.type] || msgTypeStyles.info;

  return (
    <div className={className}>
      <div
        className={[
          'p-5 rounded-3xl border-2 shadow-md transition-all',
          style.bg,
          style.border,
        ].join(' ')}
      >
        <div className="flex items-start gap-3">
          <div className={['mt-1', style.text].join(' ')}>
            {style.icon}
          </div>

          <div className="flex-1 min-w-0">
            <p
              className={[
                'text-[13px] font-bold leading-relaxed whitespace-pre-wrap',
                style.text,
              ].join(' ')}
            >
              {dash.message}
            </p>

            {dash.link && (
              <div className="mt-4 flex justify-end">
                <a
                  href={dash.link}
                  target="_blank"
                  rel="noreferrer"
                  className={[
                    'inline-flex items-center gap-2',
                    'text-[10px] font-black px-4 py-2 rounded-full',
                    'border-2 border-current bg-white/60',
                    'hover:bg-white active:scale-[0.98] transition-all',
                    style.text,
                  ].join(' ')}
                >
                  {dash.link_text || '詳しく見る'}
                  <LinkIcon size={14} />
                </a>
              </div>
            )}
     
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardMessageBlock;